import {useCallback, useEffect, useMemo, useState, type ComponentProps} from 'react'
import ReactApexChart from 'react-apexcharts'
import type {ApexOptions} from 'apexcharts'
import {cn} from '@/lib/utils'

/** Del envoltorio de React: `ApexOptions['chart']['type']` trae variantes que este no acepta. */
type ApexChartType = ComponentProps<typeof ReactApexChart>['type']

type SegmentHandler = NonNullable<NonNullable<ApexOptions['chart']>['events']>['dataPointSelection']

// ApexCharts pinta atributos SVG, no clases, y el check `color_fuera_de_tokens` solo admite hex en `index.css`:
// los colores se leen de las custom properties con `getComputedStyle`.
const TOKENS = [
  'primary',
  'chart-planear',
  'chart-hacer',
  'chart-verificar',
  'chart-actuar',
  'tone-red-fg',
  'tone-orange-fg',
  'tone-amber-fg',
  'tone-lime-fg',
  'accent-foreground',
  'muted-foreground',
  'foreground',
  'border',
  'track',
  'card',
] as const

type ChartToken = (typeof TOKENS)[number]
export type ChartTokens = Record<ChartToken, string>

function readTokens(): ChartTokens {
  const styles = getComputedStyle(document.documentElement)
  const read = (name: ChartToken) => styles.getPropertyValue(`--${name}`).trim()
  return Object.fromEntries(TOKENS.map((name) => [name, read(name)])) as ChartTokens
}

function useMediaFlag(query: string): boolean {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches)
  useEffect(() => {
    const mql = window.matchMedia(query)
    const onChange = () => setMatches(mql.matches)
    mql.addEventListener('change', onChange)
    return () => mql.removeEventListener('change', onChange)
  }, [query])
  return matches
}

export function useChartTokens(): ChartTokens {
  // El cambio de esquema de color es la señal para releer los tokens.
  const dark = useMediaFlag('(prefers-color-scheme: dark)')
  return useMemo(() => {
    void dark
    return readTokens()
  }, [dark])
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/** Profunda: de un nivel, `yaxis.labels.formatter` de un llamador borraba `yaxis.labels.style` de la
 * base. Los arreglos se pisan enteros. */
function mergeOptions(base: ApexOptions, extra: ApexOptions): ApexOptions {
  const out: Record<string, unknown> = {...base}
  for (const [key, value] of Object.entries(extra)) {
    const previous = out[key]
    out[key] =
      isPlainObject(previous) && isPlainObject(value)
        ? mergeOptions(previous as ApexOptions, value as ApexOptions)
        : value
  }
  return out as ApexOptions
}

function baseOptions(tokens: ChartTokens, reducedMotion: boolean, fontFamily: string): ApexOptions {
  const label = {colors: tokens['muted-foreground'], fontSize: '11px', fontFamily}
  return {
    chart: {
      fontFamily,
      background: 'transparent',
      toolbar: {show: false},
      zoom: {enabled: false},
      parentHeightOffset: 0,
      // Sin `dynamicAnimation`, un cambio de datos sin remontar la gráfica es un parpadeo seco.
      animations: {
        enabled: !reducedMotion,
        speed: 420,
        dynamicAnimation: {enabled: !reducedMotion, speed: 350},
      },
    },
    dataLabels: {enabled: false},
    legend: {show: false},
    grid: {
      borderColor: tokens.border,
      strokeDashArray: 3,
      padding: {top: 0, right: 4, bottom: 0, left: 4},
    },
    stroke: {lineCap: 'round'},
    xaxis: {
      axisBorder: {show: false},
      axisTicks: {show: false},
      labels: {style: label},
      tooltip: {enabled: false},
    },
    yaxis: {labels: {style: label}},
    tooltip: {
      style: {fontSize: '12px', fontFamily},
      marker: {show: true},
    },
    states: {hover: {filter: {type: 'lighten'}}},
  }
}

export function donutCenter({
  tokens,
  label,
  total,
  size = '70%',
}: {
  tokens: ChartTokens
  label: string
  total: number
  size?: string
}): ApexOptions['plotOptions'] {
  return {
    pie: {
      donut: {
        size,
        labels: {
          show: true,
          name: {fontSize: '10px', color: tokens['muted-foreground'], offsetY: 20},
          value: {fontSize: '22px', fontWeight: 600, color: tokens.foreground, offsetY: -12},
          total: {
            show: true,
            label,
            fontSize: '10px',
            color: tokens['muted-foreground'],
            formatter: () => String(total),
          },
        },
      },
    },
  }
}

export function ApexChart({
  type,
  height,
  options,
  series,
  className,
  testId,
  clickable = false,
}: {
  type: ApexChartType
  height: number | string
  options: ApexOptions
  series: ApexOptions['series']
  className?: string
  testId?: string
  clickable?: boolean
}) {
  const tokens = useChartTokens()
  const reducedMotion = useMediaFlag('(prefers-reduced-motion: reduce)')
  const fontFamily = useMemo(
    () => getComputedStyle(document.documentElement).getPropertyValue('--font-sans').trim() || 'inherit',
    []
  )
  const merged = useMemo(
    () => mergeOptions(baseOptions(tokens, reducedMotion, fontFamily), options),
    [tokens, reducedMotion, fontFamily, options]
  )
  return (
    <div className={cn('apex-host', className)} data-testid={testId} data-clickable={clickable}>
      <ReactApexChart type={type} height={height} width="100%" options={merged} series={series} />
    </div>
  )
}

/** ApexCharts entrega el índice, nunca el dato: `dataPointIndex` en barras y áreas, `seriesIndex` en
 * donut y pie (cada porción es una serie). */
export function useSegmentNavigation<T>(keys: readonly T[], onPick: (key: T) => void): SegmentHandler {
  return useCallback<NonNullable<SegmentHandler>>(
    (_event, _chart, options) => {
      const index = (options?.dataPointIndex ?? -1) >= 0 ? options!.dataPointIndex : (options?.seriesIndex ?? -1)
      const key = keys[index]
      if (key === undefined) return
      onPick(key)
    },
    [keys, onPick]
  )
}
